import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import { UsersRound } from "lucide-react";

import messages from "../../../../assets/locale/messages";
import { formatDate } from "../../../../utils/Helpers";
import { UIButton, UIEmptyState } from "../../../ui";
import CrewSizeUpgradeFlow from "../CrewSizeUpgradeFlow";
import HowChargesWork from "../../Form/Steps/ChooseCrewSize/HowChargesWork";
import { eventStatuses } from "../../../../utils/Constants";
import {
  CREW_SIZE_SALES_EMAIL,
  EVENT_MODELS,
  fillTemplate,
  formatAmount,
  formatCrewSizePrice,
  getCrewSizeFee,
  getEventBillingModel,
  getEventCrewSizeOption,
} from "../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import {
  PAYMENT_REQUEST_STATUSES,
  isDraftEvent,
} from "../BasicInfo/paymentStateUtils";
import {
  getUpgradeRequest,
  isUpgradeableEvent,
} from "../Roles/crewSizeUpgradeUtils";
import CancelUpgradeRequestModal from "../Modals/CancelUpgradeRequestModal";
import PaymentNotice from "../PaymentNotice";
import UpdateCustomPlanModal from "../Modals/UpdateCustomPlanModal";
import UpgradeCategoryModal from "../Modals/UpgradeCategoryModal";
import { canUpgradeCustomPlan } from "./customPlanUpgradeUtils";

import "../../Form/Steps/ChooseCrewSize/ChooseCrewSize.scss";
import "./EventCrewSize.scss";

/**
 * Crew Size tab. Draft event: the saved crew size, or a prompt to choose one.
 * Pending: the submitted crew size, locked. Published (Super Admin): "Update Custom Plan"
 * on a custom plan, "Upgrade Plan" (Upgrade Category) on a standard one.
 */
const EventCrewSize = ({
  event,
  onChooseCrewSize,
  canUpdateCustomPlan,
  canRequestUpgrade,
  onRefreshEvent,
  onEventUpdated,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents.crewSizeTab;
  const crewSizeT = events.creatEvent.crewSize;

  const [updateOpen, setUpdateOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [companyRequestOpen, setCompanyRequestOpen] = useState(false);
  const [cancelRequestOpen, setCancelRequestOpen] = useState(false);
  // Company: its latest crew size change request (`last_upgrade_payment_request`).
  const upgradeRequest = canRequestUpgrade ? getUpgradeRequest(event) : null;
  const requestStatus = upgradeRequest?.status;
  const pendingUpgrade =
    requestStatus === PAYMENT_REQUEST_STATUSES.pending ? upgradeRequest : null;
  const rejectedUpgrade =
    requestStatus === PAYMENT_REQUEST_STATUSES.rejected ? upgradeRequest : null;
  const acceptedUpgrade =
    requestStatus === PAYMENT_REQUEST_STATUSES.accepted ? upgradeRequest : null;
  // Figma: 22-03-2026 in both languages.
  const dateFormat = "DD-MM-YYYY";
  const requestAmount = (request) =>
    `${formatAmount(Number(request.total_amount ?? 0))} ${crewSizeT.currency}`;

  const crewSize = getEventCrewSizeOption(event);
  const isDraft = isDraftEvent(event);
  // The tab has its own points (PROLINE-4088); the wizard step keeps the pricing guide.
  const howChargesT = {
    howChargesWork: crewSizeT.howChargesWork,
    charges: t.howChargesWork,
  };

  if (!crewSize) {
    return (
      <div className="event-crew-size-tab">
        <div className="event-crew-size__empty">
          <UIEmptyState
            icon={<UsersRound aria-hidden />}
            classNames={{ root: "ui-empty-state--embedded" }}
            title={t.emptyTitle}
            description={t.emptyDescription}
            // The crew size is only chosen on a draft; a published, completed or cancelled event has none to set.
            actionLabel={isDraft && onChooseCrewSize ? t.choose : null}
            onAction={isDraft ? onChooseCrewSize : undefined}
          />
        </div>
        {isDraft && <HowChargesWork t={howChargesT} />}
      </div>
    );
  }

  const isPending = event?.status === eventStatuses.pendingPayment;
  const isPublished = !isDraft && !isPending;
  const isPublishedCustom = crewSize.custom && isPublished;
  const isPublishedStandard = !crewSize.custom && isPublished;
  const canUpgrade = canUpdateCustomPlan && canUpgradeCustomPlan(event);
  const showUpdate = isPublishedCustom && canUpgrade;
  const showUpgrade = isPublishedStandard && canUpgrade;
  // Company: asks for a bigger standard crew size; a custom plan is changed by the sales team.
  const isUpgradeable = isUpgradeableEvent(event);
  const showCompanyUpgrade =
    canRequestUpgrade &&
    isPublishedStandard &&
    isUpgradeable &&
    !pendingUpgrade;

  // A completed or cancelled event can't change its crew size, so no note suggests it (PROLINE-4257).
  let note = t.draftNote;
  if (isPending) note = canRequestUpgrade ? t.companyPendingNote : t.pendingNote;
  else if (isPublishedCustom && canRequestUpgrade) {
    note = canUpgradeCustomPlan(event)
      ? fillTemplate(t.companyCustomNote, { email: CREW_SIZE_SALES_EMAIL })
      : null;
  } else if (isPublishedCustom) note = showUpdate ? t.publishedCustomNote : null;
  else if (isPublishedStandard && canRequestUpgrade) {
    note = null;
    if (isUpgradeable) note = rejectedUpgrade ? t.upgradeRejected.note : t.companyPublishedNote;
  } else if (isPublishedStandard) note = t.publishedStandardNote;

  const [beforeDate, afterDate] = t.upgradeRejected.message.split("{date}");
  const [beforeReason, afterReason] = afterDate.split("{reason}");

  let title = crewSize.name ?? crewSizeT.sizes[crewSize.key];
  // Figma: "26 – 75 applicants" (the wizard cards keep their own copy).
  let range =
    crewSize.custom || crewSize.min <= 1
      ? fillTemplate(t.upTo, { max: formatAmount(crewSize.max) })
      : fillTemplate(t.range, {
          min: formatAmount(crewSize.min),
          max: formatAmount(crewSize.max),
        });
  // A custom rate is per day or one flat amount, by the event's `payment_plan` (PROLINE-4075).
  const model = getEventBillingModel(event, event?.operating_days);
  const price = formatCrewSizePrice(crewSizeT, getCrewSizeFee(crewSize, model), model);
  const rate =
    price &&
    fillTemplate(
      model === EVENT_MODELS.extended ? t.publishedEventRate : t.publishedDailyRate,
      { price },
    );
  // Super Admin view; the company keeps the plan name and range (Figma).
  if (isPublishedCustom && !canRequestUpgrade) {
    title = fillTemplate(t.updateCustomPlan.customizedUpTo, {
      max: formatAmount(crewSize.max),
    });
    range = null;
  }

  return (
    <div className="event-crew-size-tab">
      <div className="event-crew-size">
        <div className="event-crew-size__card">
          <Typography
            component="h2"
            className="text-heading-sm event-crew-size__title"
          >
            {title}
          </Typography>
          {range && (
            <Typography
              component="p"
              className="text-body-sm event-crew-size__range"
            >
              {range}
            </Typography>
          )}
          {rate && (
            <Typography
              component="p"
              className="text-label-sm event-crew-size__rate"
            >
              {rate}
            </Typography>
          )}
        </div>

        {acceptedUpgrade && (
          <Typography
            component="p"
            className="text-body-sm event-crew-size__note"
          >
            {fillTemplate(t.upgradeAccepted, {
              size: acceptedUpgrade.crew_size_plan?.name ?? "",
              date: formatDate(acceptedUpgrade.reviewed_at, dateFormat),
              amount: requestAmount(acceptedUpgrade),
            })}
          </Typography>
        )}

        {pendingUpgrade && (
          <PaymentNotice
            className="event-crew-size__notice"
            title={fillTemplate(t.upgradePending.title, {
              size: pendingUpgrade.crew_size_plan?.name ?? "",
            })}
            description={fillTemplate(t.upgradePending.message, {
              date: formatDate(pendingUpgrade.created_at, dateFormat),
            })}
          />
        )}

        {rejectedUpgrade && (
          <PaymentNotice
            variant="error"
            className="event-crew-size__notice"
            title={fillTemplate(t.upgradeRejected.title, {
              size: rejectedUpgrade.crew_size_plan?.name ?? "",
            })}
            description={
              <>
                {beforeDate}
                {/* Next to Arabic text the browser would read it backwards: 2026-10-04. */}
                <bdi dir="ltr">{formatDate(rejectedUpgrade.reviewed_at, dateFormat)}</bdi>
                {beforeReason}
                {/* Typed in either language, with the refund sentence right after it. */}
                <bdi>{rejectedUpgrade.rejection_reason ?? ""}</bdi>
                {afterReason}
              </>
            }
          />
        )}

        {/* No upgrade can be requested while one waits for approval. */}
        {!pendingUpgrade && note && (
          <Typography
            component="p"
            className="text-body-sm event-crew-size__note"
          >
            {note}
          </Typography>
        )}

        {showCompanyUpgrade && (
          <UIButton
            type="button"
            {...(rejectedUpgrade
              ? { variant: "primary", size: "medium" }
              : { variant: "outlined", color: "primary", appearance: "outline" })}
            className="event-crew-size__action"
            onClick={() => setCompanyRequestOpen(true)}
          >
            {rejectedUpgrade ? t.upgradeRejected.retry : t.companyUpgradeAction}
          </UIButton>
        )}
        {pendingUpgrade && (
          <UIButton
            type="button"
            variant="outlined"
            className="event-crew-size__action"
            onClick={() => setCancelRequestOpen(true)}
          >
            {t.upgradePending.cancel}
          </UIButton>
        )}

        {(showUpdate || showUpgrade) && (
          <UIButton
            type="button"
            variant="outlined"
            color="primary"
            appearance="outline"
            className="event-crew-size__action"
            onClick={() =>
              showUpdate ? setUpdateOpen(true) : setUpgradeOpen(true)
            }
          >
            {showUpdate ? t.updateCustomPlan.action : t.upgradeCategory.action}
          </UIButton>
        )}
      </div>

      {isDraft && <HowChargesWork t={howChargesT} />}

      {/* Mounted on open so the fields start from the current plan each time. */}
      {updateOpen && (
        <UpdateCustomPlanModal
          open
          event={event}
          currentPlan={crewSize}
          onClose={() => setUpdateOpen(false)}
          onUpdated={(updatedEvent) => {
            setUpdateOpen(false);
            onEventUpdated?.(updatedEvent);
          }}
        />
      )}
      {companyRequestOpen && (
        <CrewSizeUpgradeFlow
          event={event}
          currentCrewSize={crewSize}
          onClose={() => setCompanyRequestOpen(false)}
          onSubmitted={() => {
            setCompanyRequestOpen(false);
            onRefreshEvent?.({ silent: true });
          }}
        />
      )}
      {pendingUpgrade && (
        <CancelUpgradeRequestModal
          open={cancelRequestOpen}
          requestId={pendingUpgrade.id}
          onSettled={() => {
            setCancelRequestOpen(false);
            onRefreshEvent?.({ silent: true });
          }}
        />
      )}
      {upgradeOpen && (
        <UpgradeCategoryModal
          open
          event={event}
          currentPlan={crewSize}
          onClose={() => setUpgradeOpen(false)}
          onUpdated={(updatedEvent) => {
            setUpgradeOpen(false);
            onEventUpdated?.(updatedEvent);
          }}
        />
      )}
    </div>
  );
};

EventCrewSize.propTypes = {
  event: PropTypes.object,
  /** Opens the wizard at the Crew Size step; omit to hide the action. */
  onChooseCrewSize: PropTypes.func,
  /** Super Admin: may upgrade the crew size (standard or custom plan) of a published event. */
  canUpdateCustomPlan: PropTypes.bool,
  /** Company: may request a bigger standard crew size for a published event. */
  canRequestUpgrade: PropTypes.bool,
  /** Reloads the event once an upgrade request is submitted or cancelled. */
  onRefreshEvent: PropTypes.func,
  /** Super Admin: called with the event the backend returns after an upgrade is saved. */
  onEventUpdated: PropTypes.func,
};

EventCrewSize.defaultProps = {
  event: null,
  onChooseCrewSize: undefined,
  canUpdateCustomPlan: false,
  canRequestUpgrade: false,
  onRefreshEvent: undefined,
  onEventUpdated: undefined,
};

export default EventCrewSize;
